import {FACTIONS} from "../../../constants/factions";
import {UNIT_TYPES, getUnitTypeConfig} from "../../../configs/units";
import {
  SUCCUBES_SEDUCTION_COST,
  SUCCUBES_SEDUCTION_RANGE,
  SUCCUBES_SEDUCTION_SUCCESS_THRESHOLD,
} from "../../../configs/factions/succubes";
import {
  W39_SUCCUBES_CITY_CAPTURE_COINS,
  W39_SUCCUBES_MIN_CHANCE,
  W39_SUCCUBES_MIN_CHANCE_SPARE,
  W39_SUCCUBES_POSITION_MAX,
  W39_SUCCUBES_POSITION_WEIGHT,
  W39_SUCCUBES_RESERVE_MAX_ATTEMPTS,
  W39_SUCCUBES_RESERVE_REACH,
  W39_SUCCUBES_SEDUCER_MIN_SIZE,
  W39_SUCCUBES_SIZE_COINS,
  W39_SUCCUBES_SPARE_COINS,
} from "../../../configs/botW39Factions";
import {getHexDistance} from "../../coords/getHexDistance";
import {hasAdjacentEnemyUnit} from "../../gameState/hasAdjacentEnemyUnit";
import {hasObstacleInPath} from "../../gameState/shooting/calculateAvailableShootingCells";
import {calculateAvailableSeductionCells} from "../../factions/succubes/calculateAvailableSeductionCells";
import {getAllQuartersOfCity} from "../../gameState/getAllQuartersOfCity";
import {checkIfW39FactionPlay} from "./w39FactionPlay";

// Суккубы волны 39 (developer_artifacts/bot_strategy/faction_strategy/succubes.md)

export const checkIfW39SuccubesPlayer = (player) => checkIfW39FactionPlay(player, FACTIONS.SUCCUBES);

/**
 * Шанс успеха соблазнения: d20 + (размер соблазнителя − размер цели) ≥ порога
 * (calculateSeductionSuccess). Бросок честный, 1–20
 */
export const calcW39SeductionChance = (attackerSize, targetSize) => {
  const minRoll = SUCCUBES_SEDUCTION_SUCCESS_THRESHOLD - (attackerSize - targetSize);
  if (minRoll <= 1) {
    return 1;
  }
  if (minRoll > 20) {
    return 0;
  }
  return (21 - minRoll) / 20;
};

// Отряд суккубов, способный соблазнять: фракция отряда (не владельца), не
// катапульта, размер не ниже порога соблазнителя
const checkIfSeducerUnit = (unit) => unit.factionId === FACTIONS.SUCCUBES
  && unit.type !== UNIT_TYPES.D4.id
  && unit.size >= W39_SUCCUBES_SEDUCER_MIN_SIZE;

/**
 * Карта «квартал → город» по всем городам поля (свои, чужие, нейтральные):
 * {color, city, quarters}
 */
export const createW39QuarterCityMap = (citiesCache) => {
  const quarterCities = new Map();
  Object.entries(citiesCache || {}).forEach(([color, cities]) => {
    (cities || []).forEach((city) => {
      const quarters = getAllQuartersOfCity(city);
      const entry = {color, city, quarters};
      quarters.forEach((quarterHexKey) => quarterCities.set(quarterHexKey, entry));
    });
  });
  return quarterCities;
};

/**
 * Решает ли соблазнение цели судьбу города (checkCityCapture: город захвачен,
 * если на его кварталах нет отрядов владельца и есть отряды ровно одного
 * другого игрока):
 *  - чужой или нейтральный город, где цель — единственный не наш отряд на
 *    кварталах: после соблазнения город перейдет нам в конце раунда
 *    («бескровный захват», succubes.md);
 *  - свой город, где на кварталах нет наших отрядов, а цель — единственный
 *    чужой: соблазнение снимает захват.
 */
export const checkIfW39SeductionDecidesCity = (target, seducerOwner, units, quarterCities) => {
  const cityEntry = quarterCities.get(target.hexKey);
  if (!cityEntry) {
    return false;
  }
  const quarterSet = new Set(cityEntry.quarters);
  const othersOnQuarters = units.filter((unit) => unit.id !== target.id && quarterSet.has(unit.hexKey));
  if (cityEntry.color === seducerOwner.color) {
    return othersOnQuarters.length === 0;
  }
  return othersOnQuarters.every((unit) => unit.ownerId === seducerOwner.id);
};

/** Ценность цели в монетах: цена найма ее типа + размер по цене докупки размера */
export const calcW39SeductionTargetValue = (target, decidesCity) => {
  const unitCoins = (getUnitTypeConfig(target.type)?.price || 0) + target.size * W39_SUCCUBES_SIZE_COINS;
  return unitCoins + (decidesCity ? W39_SUCCUBES_CITY_CAPTURE_COINS : 0);
};

/**
 * Ожидаемая ценность попытки (монеты): шанс × ценность − цена попытки. null —
 * попытка не оправдана: шанс ниже порога (W39_SUCCUBES_MIN_CHANCE; при запасе
 * монет сверх W39_SUCCUBES_SPARE_COINS — ниже W39_SUCCUBES_MIN_CHANCE_SPARE) или
 * ожидаемая ценность не положительна
 */
export const evaluateW39SeductionAttempt = ({attackerSize, target, decidesCity, coins}) => {
  const chance = calcW39SeductionChance(attackerSize, target.size);
  const minChance = coins - SUCCUBES_SEDUCTION_COST >= W39_SUCCUBES_SPARE_COINS
    ? W39_SUCCUBES_MIN_CHANCE_SPARE
    : W39_SUCCUBES_MIN_CHANCE;
  if (chance < minChance) {
    return null;
  }
  const expectedValue = chance * calcW39SeductionTargetValue(target, decidesCity) - SUCCUBES_SEDUCTION_COST;
  return expectedValue > 0 ? {chance, expectedValue} : null;
};

/**
 * Лучшая попытка соблазнения этапа способностей: {unit, target, chance,
 * expectedValue} или null. Перебираются все свои отряды суккубов, которые
 * могут соблазнять (правила — calculateAvailableSeductionCells: фракция, не
 * катапульта, способность не применена, не зажат, радиус и прямая видимость)
 */
export const pickW39SeductionAttempt = ({player, units, hexes, specialBuildings, buildingsDamage,
  fortressesUnderConstruction, citiesCache}) => {
  if (!player || (player.coins || 0) < SUCCUBES_SEDUCTION_COST) {
    return null;
  }
  const quarterCities = createW39QuarterCityMap(citiesCache);
  let best = null;

  units.forEach((unit) => {
    if (unit.ownerId !== player.id || unit.factionId !== FACTIONS.SUCCUBES) {
      return;
    }
    const targetCells = calculateAvailableSeductionCells(
      hexes, units, unit, specialBuildings, buildingsDamage, fortressesUnderConstruction
    );
    targetCells.forEach((targetHexKey) => {
      const target = units.find((otherUnit) => otherUnit.hexKey === targetHexKey);
      if (!target) {
        return;
      }
      const evaluation = evaluateW39SeductionAttempt({
        attackerSize: unit.size,
        target,
        decidesCity: checkIfW39SeductionDecidesCity(target, player, units, quarterCities),
        coins: player.coins,
      });
      if (evaluation && (!best || evaluation.expectedValue > best.expectedValue)) {
        best = {unit, target, ...evaluation};
      }
    });
  });

  return best;
};

/**
 * Контекст оценки позиций соблазнителей в перемещении (один на вызов
 * decideMovementStep) или null, если игрок не суккуб волны 39 или монет на
 * попытку нет
 */
export const createW39SuccubesMovementContext = ({player, units, hexes, specialBuildings, buildingsDamage,
  fortressesUnderConstruction, citiesCache}) => {
  if (!checkIfW39SuccubesPlayer(player) || (player.coins || 0) < SUCCUBES_SEDUCTION_COST) {
    return null;
  }
  return {
    player,
    hexes,
    specialBuildings,
    buildingsDamage,
    fortressesUnderConstruction,
    quarterCities: createW39QuarterCityMap(citiesCache),
  };
};

/**
 * Надбавка позиции отряда-соблазнителя: лучшая ожидаемая ценность соблазнения
 * с этой клетки (цели на дистанции 2..радиус с прямой видимостью; смежный
 * чужой отряд запрещает способность — тогда 0) × вес, с потолком. Соблазнение
 * идет в том же ходу сразу после перемещения (этап способностей)
 */
export const scoreW39SeducerPosition = (unit, cellKey, units, context) => {
  if (!context || unit.ownerId !== context.player.id || !checkIfSeducerUnit(unit)) {
    return 0;
  }
  // Сам отряд на клетке cellKey: смежность считаем без него
  const otherUnits = units.filter((otherUnit) => otherUnit.id !== unit.id);
  if (hasAdjacentEnemyUnit(cellKey, unit.ownerId, otherUnits)) {
    return 0;
  }
  const movedUnits = [...otherUnits, {...unit, hexKey: cellKey}];
  let bestValue = 0;
  otherUnits.forEach((target) => {
    if (target.ownerId === unit.ownerId) {
      return;
    }
    const distance = getHexDistance(cellKey, target.hexKey);
    if (distance < 2 || distance > SUCCUBES_SEDUCTION_RANGE) {
      return;
    }
    const evaluation = evaluateW39SeductionAttempt({
      attackerSize: unit.size,
      target,
      decidesCity: checkIfW39SeductionDecidesCity(target, context.player, movedUnits, context.quarterCities),
      coins: context.player.coins,
    });
    if (!evaluation || evaluation.expectedValue <= bestValue) {
      return;
    }
    if (hasObstacleInPath(cellKey, target.hexKey, context.hexes, context.specialBuildings, [],
      context.buildingsDamage, context.fortressesUnderConstruction)) {
      return;
    }
    bestValue = evaluation.expectedValue;
  });
  return Math.min(W39_SUCCUBES_POSITION_MAX, bestValue * W39_SUCCUBES_POSITION_WEIGHT);
};

/**
 * Резерв монет под соблазнение этого хода (succubes.md, «правило резерва
 * монет»): цена попытки за каждого соблазнителя, у которого в
 * W39_SUCCUBES_RESERVE_REACH есть чужой отряд с шансом не ниже порога, — не
 * больше W39_SUCCUBES_RESERVE_MAX_ATTEMPTS попыток. Нейтралы тоже цели
 * (гарнизоны нейтральных городов). 0 — у не-суккубов волны 39
 */
export const calcW39SeductionCoinReserve = (player, units) => {
  if (!checkIfW39SuccubesPlayer(player)) {
    return 0;
  }
  const seducersWithTargets = units.filter((unit) => unit.ownerId === player.id
    && checkIfSeducerUnit(unit)
    && units.some((target) => target.ownerId !== player.id
      && getHexDistance(unit.hexKey, target.hexKey) <= W39_SUCCUBES_RESERVE_REACH
      && calcW39SeductionChance(unit.size, target.size) >= W39_SUCCUBES_MIN_CHANCE)
  ).length;
  return Math.min(W39_SUCCUBES_RESERVE_MAX_ATTEMPTS, seducersWithTargets) * SUCCUBES_SEDUCTION_COST;
};
